import Swal from "sweetalert2";

/**
 * Types of Swal modals with predefined configurations.
 */
export const InCoreSwal2Type = {
    Message: {
        customClass: {
            confirmButton: "btn btn-primary",
        },
        buttonsStyling: false,
        title: "",
        text: "",
        html: "",
    },
    ConfirmCancel: {
        showConfirmButton: true,
        showCancelButton: true,
        customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-empty",
        },
        buttonsStyling: false,
        title: "",
        text: "",
        html: "",
    },
    ConfirmDeny: {
        showConfirmButton: true,
        showDenyButton: true,
        customClass: {
            confirmButton: "btn btn-success",
            denyButton: "btn btn-danger",
        },
        buttonsStyling: false,
        title: "",
        text: "",
        html: "",
    },
    ConfirmCancelDeny: {
        showConfirmButton: true,
        showCancelButton: true,
        showDenyButton: true,
        customClass: {
            confirmButton: "btn btn-success",
            cancelButton: "btn btn-empty",
            denyButton: "btn btn-danger",
        },
        buttonsStyling: false,
        title: "",
        text: "",
        html: "",
    },
};

/**
 * Icons to display in Swal modals.
 */
export const InCoreSwal2Icon = {
    None: "",
    Success: "success",
    Error: "error",
    Warning: "warning",
    Info: "info",
    Question: "question",
};

/**
 * Advanced Swal modal activation.
 * @param {any} type - The type of Swal modal.
 * @param {any} icon - The icon to display in the Swal modal.
 * @param {any} title - The title of the Swal modal.
 * @param {any} body - The content of the Swal modal.
 * @param {boolean} ishtml - Whether the content is HTML.
 * @param {string} confirmtext - Text for the confirm button.
 * @param {string} canceltext - Text for the cancel button.
 * @param {string} denytext - Text for the deny button.
 * @returns {Promise<any>} - The user's response.
 */
export async function InCoreSwal2X(
    type: any,
    icon: any,
    title: string,
    body: string,
    ishtml: boolean,
    confirmtext: string,
    canceltext: string,
    denytext: string
): Promise<any> {
    const param = { ...type };
    if (icon) param.icon = icon;
    if (title) param.title = title;
    if (body) {
        if (ishtml) param.html = body;
        else param.text = body;
    } else {
        param.html = "";
        param.text = "";
    }
    if (confirmtext) param.confirmButtonText = confirmtext;
    if (canceltext) param.cancelButtonText = canceltext;
    if (denytext) param.denyButtonText = denytext;

    return await FireSwal(param);
}

/**
 * Standard Swal modal activation.
 * @param {any} icon - The icon to display in the Swal modal.
 * @param {string} body - The content of the Swal modal.
 * @returns {Promise<any>} - The user's response.
 */
export async function InCoreSwal2(icon: any, body: string): Promise<any> {
    let param: any = {};

    param.confirmButtonText = "Ok";

    switch (icon) {
        case InCoreSwal2Icon.Success:
            param = { ...InCoreSwal2Type.Message, title: "Successo" };
            break;
        case InCoreSwal2Icon.Error:
            param = { ...InCoreSwal2Type.Message, title: "Errore" };
            break;
        case InCoreSwal2Icon.Warning:
            param = { ...InCoreSwal2Type.Message, title: "Attenzione" };
            break;
        case InCoreSwal2Icon.Info:
            param = { ...InCoreSwal2Type.Message, title: "Informazione" };
            break;
        case InCoreSwal2Icon.Question:
            param = { ...InCoreSwal2Type.ConfirmCancelDeny, title: "Domanda", confirmButtonText: "Si" };
            break;
        default:
            param = { ...InCoreSwal2Type.Message, title: "" };
    }
    param.icon = icon;
    param.html = body;
    param.cancelButtonText = "Annulla";
    param.denyButtonText = "No";

    return await FireSwal(param);
}

/**
 * Custom Swal modal activation.
 * @param {any} param - All parameters compatible with the Swal library.
 * @returns {Promise<any>} - The user's response.
 */
export async function InCoreSwal2Custom(param: any): Promise<any> {
    return await FireSwal(param);
}

/**
 * Custom Swal modal activation with partial parameter overrides.
 * @param {any} param - The base Swal parameters.
 * @param {any} delta - Key-value pairs to override in the base parameters.
 * @returns {Promise<any>} - The user's response.
 */
export async function InCoreSwal2CustomPartial(param: any, delta: any): Promise<any> {
    const updatedParam = { ...param, ...delta };
    return await FireSwal(updatedParam);
}

/**
 * Fires a Swal modal with auto height adjustment.
 * @param {any} param - All parameters compatible with the Swal library.
 * @returns {Promise<any>} - The user's response.
 */
export function FireSwal(param: any): Promise<any> {
    param.heightAuto = false;
    return Swal.fire(param);
}